import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import NavMenu from "components/NavMenu/NavMenu";
import useUserContext from "Hooks/useUserContext";
import styles from "components/Layout.module.css";

/** @component renders the {@link NavMenu}, appropriate
 * background color, and children components (rest of the site).
 */
export default function Layout(props: { children: React.ReactNode }) {
    const [userContext] = useUserContext();
    const url = useLocation().pathname;

    // Saves last page to local storage if user is a Calance admin.
    // Info gets read and used in Autologin.tsx
    useEffect(() => {
        const excludedUrls = [
            "/activate_user",
            "/admin/login_as",
            "/login_company",
            "/login",
            "/not_found",
            "/run_details",
            "/",
            "",
        ];

        // Note: match_tools & run_details can't be added to excludedUrls because they have a
        // dynamic route param. Even when logic was rewritten to check if each excludedUrl
        // is contained in url, it still won't work because then "/" and "" are in every route.
        if (
            userContext.isCalanceAdmin &&
            !excludedUrls.includes(url) &&
            !url.includes("match_tools") &&
            !url.includes("run_details")
        ) {
            localStorage.setItem("lastPage", url);
        }
    }, [url, userContext.isCalanceAdmin]);

    // Set site title based on environment and login company
    useEffect(() => {
        if (userContext.environment && userContext.companyName) {
            document.title = `${
                userContext.environment[0].toUpperCase() +
                userContext.environment.slice(1)
            } - ${userContext.companyName}`;
        }
    }, [userContext.companyName, userContext.environment]);

    const getBackgroundClass = () => {
        if (url.includes("not_found")) {
            return "notFoundBackground";
        }
        return "whiteBackground";
    };

    return (
        <div>
            {/* Nav bar / menu */}
            <NavMenu />
            <div
                className={getBackgroundClass()}
                style={{ paddingTop: "30px", paddingBottom: "30px" }}
            >
                {/* Page content */}
                <main style={{ minHeight: "76vh" }}>{props.children}</main>
                {/* Footer / copyright & build version */}
                <footer>
                    {userContext.machineName && (
                        <div className={styles.buildNumberBox}>
                            <div>
                                Copyright @ {new Date().getFullYear()}{" "}
                                <a href="https://www.calanceus.com/services/project-control-integrations">
                                    Calance
                                </a>
                            </div>
                            {(userContext.buildDate || userContext.gitHash) && (
                                <div>Build: {userContext.buildDate}{userContext.gitHash && ` · ${userContext.gitHash}`}</div>
                            )}
                            <div>Server: {userContext.machineName}</div>
                        </div>
                    )}
                    {url.includes("login") && (
                        <div className={styles.freepikAttribution}>
                            Illustrations designed by{" "}
                            <a
                                className={styles.freepikLink}
                                href="www.freepik.com"
                            >
                                Freepik
                            </a>
                        </div>
                    )}
                </footer>
            </div>
        </div>
    );
}
